import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { Job } from '../types';

// Jobs API path (relative to axios baseURL which is /api/v1/goodday)
const JOBS_API = '/jobs';

/** Canonical jobs key — shared by the Scraper console history AND the dashboard widget. */
export const JOBS_KEY = ['jobs', 'list'] as const;

interface RawJob extends Partial<Job> {
  _id?: string;
}

/**
 * Normalize a job record. `Jobs.listForUser` returns raw docs (only `_id`),
 * while `Jobs.listAll` maps `id` — so we normalize defensively here.
 */
function normalizeJob(j: RawJob): Job {
  return {
    id: j.id ?? j._id ?? '',
    niche: j.niche ?? '',
    location: j.location ?? '',
    status: (j.status as Job['status']) ?? 'completed',
    leads_found: j.leads_found ?? 0,
    duplicates_skipped: j.duplicates_skipped ?? 0,
    error_message: j.error_message,
    started_at: j.started_at,
    finished_at: j.finished_at,
    owner_name: j.owner_name,
    owner_username: j.owner_username,
  };
}

/**
 * Single canonical jobs hook. The dashboard "Running Jobs" widget passes a
 * refetchInterval while mounted; the Scraper history reads the same cache.
 * One key → one cache → one logical request.
 */
export function useJobs(options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: JOBS_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ data: RawJob[]; total: number; limit: number; offset: number }>(JOBS_API);
      return (data.data ?? []).map(normalizeJob);
    },
    staleTime: 15_000,
    refetchInterval: options?.refetchInterval,
  });
}

export function useJobMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['jobs'] });

  const deleteJob = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`${JOBS_API}/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  const stopJob = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ ok: boolean }>(`${JOBS_API}/${id}/stop`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { deleteJob, stopJob };
}